import { autoinject, transient, computedFrom, BindingEngine, useView } from "aurelia-framework";
import { I18N } from "aurelia-i18n";
import { DataType, DataGridColumn, SourceType } from "../../../../../../models/queries/queries.models";
import { MobileHelper } from "../../../../../../helpers/mobile-helper";
import * as moment from "moment";
import { CustomGridPageBase } from "../../../../common/custom-grid-page.base";
import { UserClaimsService } from "../../../../../../services/authentication/user-claims.service";
import { Claims } from "../../../../../../constants/authentication/claims";
import { VesselHelper } from "../../../../../../models/vessels/vessel.models";
import { RangeSelectorHelper } from "../../../../../components/range-selector/range-selector";
import { CompanySubscoreDetails } from "../../../../../../models/companies/company.models";
import { CompanyService } from "../../../../../../services/modules/company-client.service";
import { CloneHelper } from "@rightship-tech/common-ui/helpers/clone.helper";
import * as Enumerable from "linq";
import { Reflection } from "../../../../../../utilities/reflection";
import { SubscriptionProductCodes } from "../../../../../../models/customers/customer-subscription.models";
import { CustomerSubscriptionService } from "../../../../../../services/modules/customer-subscription-client.service";
import { SubscriptionTypeEnum } from "@rightship-tech/common-ui/models/rightstore/rightstore.models";
import { CustomerVesselService } from "../../../../../../services/vessels/customer-vessels.service";

@autoinject
@transient()
@useView('views/pages/modules/company/steps/company-vessel-performance/company-vessel-performance.html')
export class CompanyVesselPerformance extends CustomGridPageBase {

    companyId: number = null;
    public rightShipUser: boolean;
    public canDownloadDOCPerformance: boolean;
    public canAccessDOCFleetOverview: boolean;
    public canAccessDOCFleetTable: boolean;
    public isLimitedSubscription: boolean;
    public isCustomer: boolean;
    public customerVesselIds: number[];
    public subscriptionTypeId: number;

    constructor(i18N: I18N,protected bindingEngine: BindingEngine, userClaimsService: UserClaimsService, private companyClientService: CompanyService,
        private customerSubscriptionService: CustomerSubscriptionService, private customerVesselsService: CustomerVesselService) {
        super(i18N, 'Activity', bindingEngine);

        this.rightShipUser = userClaimsService.hasPermission(Claims.RightShipUser);
        this.canDownloadDOCPerformance = userClaimsService.hasPermission(Claims.CanDownloadDOCPerformance);
        this.canAccessDOCFleetOverview = userClaimsService.hasPermission(Claims.CanAccessDOCFleetOverview);
        this.canAccessDOCFleetTable = userClaimsService.hasPermission(Claims.CanAccessDOCFleetTable);
    }

    item: CompanySubscoreDetails;
    async activate(model: CompanySubscoreDetails) {
       
        if (model.companyId){
            this.companyId = model.companyId;
            this.item = await this.companyClientService.getCompanySubscoreDetail(model.companyId);
        }
        else
        {
            this.item = model;
        }

        const response = await this.customerSubscriptionService.getMySubscription(SubscriptionProductCodes.informationServices);
        this.subscriptionTypeId = response?.customerSubscription?.subscriptionTypeId;
        this.isLimitedSubscription = response?.customerSubscription?.subscriptionTypeId == SubscriptionTypeEnum.LimitedSubscription;
        if (this.isCustomer) {
            const customerVessels = await this.customerVesselsService.getCustomerVessels();
            this.customerVesselIds = Enumerable.from(customerVessels.vessels).select(x => x.vesselId).toArray();
        }

        this.columns = [];
        this.columns.push(
            <DataGridColumn>{
                dataType: DataType.CustomTemplate,
                value: '<div class="column">' +
                    '<a class="link" href="/vessel/${item.vesselId}">${item.name}</a>' +
                    '<span if.bind="item.internationalMaritimeOrganizationNumber">IMO ${item.internationalMaritimeOrganizationNumber}</span>' +
                    '<span if.bind="item.vehicleIdentificationNumber">VIN ${item.vehicleIdentificationNumber}</span>' +
                    '<span if.bind="item.europeanNumberOfIdentification">ENI ${item.europeanNumberOfIdentification}</span>' +
                    '</div>',
                name: "name",
                title: MobileHelper.isMobileDevice() && window.innerWidth < 1024 ? "" : "Name",
                instancePerRow: true,
                sortOrder: 2,
                columnOrder: -1,
                isHidden: false,
                isSortable: true,
                parameter: {
                    item: null
                }
            },
            <DataGridColumn>{
                dataType: DataType.CustomTemplate,
                value: '<comp-safety-score score.bind="item.safetyRating" inspection.bind="item" safety-rating-indicative.bind="item.safetyRatingIndicative" safety-score-type.bind="item.safetyRatingType"' +
                    'has-error.bind="item.hasError" is-condensed="true" incident-grace-periods.bind="getIncidentGracePeriods(item)"' +
                    'unlock-feature-key="vessels" locked-feature.bind="lockedFeature" mask-data.bind="maskData(item.vesselId)"></comp-safety-score>',
                name: "safetyRating",
                title: MobileHelper.isMobileDevice() && window.innerWidth < 1024 ? "" : "Safety Score",
                instancePerRow: true,
                sortOrder: 1,
                columnOrder: -1,
                isHidden: false,
                isSortable: true,
                parameter: {
                    item: null,
                    lockedFeature: this.isLimitedSubscription,
                    maskData: (vesselId) => {
                        return this.subscriptionTypeId === SubscriptionTypeEnum.FullSubscription ? false : this.isCustomer && this.customerVesselIds.indexOf(vesselId) < 0;
                    },
                    getIncidentGracePeriods: (item) => {
                        return item.incidentGracePeriodJsonData ? JSON.parse(item.incidentGracePeriodJsonData) : [];
                    }
                },
                width: MobileHelper.isMobileDevice() && window.innerWidth < 1024 ? "" : "220px"
            },
            <DataGridColumn>{
                dataType: DataType.Number,
                name: "pscInspectionCount",
                title: "PSC Insp",
                sortOrder: 5,
                columnOrder: 5,
                isHidden: false,
                isSortable: true,
                width: MobileHelper.isMobileDevice() && window.innerWidth < 1024 ? "" : "60px"
            },
            <DataGridColumn>{
                dataType: DataType.Number,
                name: "detentions",
                title: "Det",
                sortOrder: 5,
                columnOrder: 5,
                isHidden: false,
                isSortable: true,
                width: MobileHelper.isMobileDevice() && window.innerWidth < 1024 ? "" : "60px"
            },
            <DataGridColumn>{
                dataType: DataType.Number,
                name: "totalDeficiencies",
                title: "Def",
                sortOrder: 5,
                columnOrder: 5,
                isHidden: false,
                isSortable: true,
                width: MobileHelper.isMobileDevice() && window.innerWidth < 1024 ? "" : "60px"
            },
            <DataGridColumn>{
                dataType: DataType.Number,
                name: "incidentCatACount",
                title: "Cat A Inc",
                sortOrder: 5,
                columnOrder: 5,
                isHidden: false,
                isSortable: true,
                width: MobileHelper.isMobileDevice() && window.innerWidth < 1024 ? "" : "60px"
            },
            <DataGridColumn>{
                dataType: DataType.Number,
                name: "incidentCatBCount",
                title: "Cat B Inc",
                sortOrder: 5,
                columnOrder: 5,
                isHidden: false,
                isSortable: true,
                width: MobileHelper.isMobileDevice() && window.innerWidth < 1024 ? "" : "60px"
            },
            <DataGridColumn>{
                dataType: DataType.Number,
                name: "incidentCatCCount",
                title: "Cat C Inc",
                sortOrder: 5,
                columnOrder: 5,
                isHidden: false,
                isSortable: true,
                width: MobileHelper.isMobileDevice() && window.innerWidth < 1024 ? "" : "60px"
            },
            <DataGridColumn>{
                dataType: DataType.DateTime,
                name: "dateTo",
                title: "Under DOC Management",
                sortOrder: 0,
                columnOrder: 5,
                isHidden: false,
                isSortable: true,
                valueConverter: (x, y) => `${moment(y.dateFrom).format('D MMM, YYYY')} - ${y.dateTo != this.today ? moment(y.dateTo).format('D MMM, YYYY') : 'Current'}`
            });
    }

    attached() 
    {
        this.reloadFilters();
    }

    getBuilt(dateOfBuilt?: Date): string {
        if (!dateOfBuilt) return '';

        const date = moment(dateOfBuilt);
        const yearAge = VesselHelper.getAgeFromDate(date);
        return yearAge;
    }

    private today = new Date().toISOString();

    public async reloadFilters() {
        this.isLoading = true;
        try
        {
            const request = this.getFilters();
            let response = await this.companyClientService.getDocFleet(request);
            
            response.filter(x => !x.dateTo).forEach(x => {
                x.dateTo = this.today;
            });

            response = Enumerable.from(response).orderByDescending(x => x.dateTo).toArray();

            this.itemSource = {
                sourceType: SourceType.Collection,
                source: response,
                columns: this.columns
            };
        }
        finally { this.isLoading = false; }        
    }

    getFilters() {
        const request = {
            companyId: this.item.companyId,
            minPscInspectionCount: null,
            maxPscInspectionCount: null,
            minIncidentCount: null,
            maxIncidentCount: null,
            minDetentions: null,
            maxDetentions: null,
            minTotalDeficiencies: null,
            maxTotalDeficiencies: null,
            date: [],
            includePerviouslyUnderDoc: false,
            onlyUsedInSafetyScore: false
        };

        //#region insp values
        if (this.inspFirstFilter || this.inspSecondFilter) {
            request.minPscInspectionCount = this.inspFirstFilter ? this.inspFirstFilter : 0;
            request.maxPscInspectionCount = this.inspSecondFilter ? this.inspSecondFilter : 0;
        }

        //#endregion

        //#region inc values
        if (this.incFirstFilter || this.incSecondFilter) {
            request.minIncidentCount = this.incFirstFilter ? this.incFirstFilter : 0;
            request.maxIncidentCount = this.incSecondFilter ? this.incSecondFilter : 0;
        }

        //#endregion

        //#region det values

        if (this.detFirstFilter || this.detSecondFilter) {
            request.minDetentions = this.detFirstFilter ? this.detFirstFilter : 0;
            request.maxDetentions = this.detSecondFilter ? this.detSecondFilter : 0;
        }

        //#endregion

        //#region defs values

        if (this.defsFirstFilter || this.defsSecondFilter) {
            request.minTotalDeficiencies = this.defsFirstFilter ? this.defsFirstFilter : 0;
            request.maxTotalDeficiencies = this.defsSecondFilter ? this.defsSecondFilter : 0;
        }

        //#endregion

        //#region date range values

        if (this.dateRangeFilterActive) {
            request.date = [];
            this.selectedDateRanges.forEach((dateId) => {
                const dateRange = this.dateRanges.find(x => x.id == dateId);
                request.date.push({
                    min: dateRange.dateFrom.toDate(),
                    max: dateRange.dateTo.toDate()
                });
            });
        }

        //#endregion

        request.includePerviouslyUnderDoc = this.onlyUsedInSafetyScore ? true : this.includePerviouslyUnderDoc;
        request.onlyUsedInSafetyScore = this.onlyUsedInSafetyScore;

        return request;
    }

    onlyUsedInSafetyScore: boolean = true;
    includePerviouslyUnderDoc: boolean = true;

    changeOnlyUsedInSafetyScore(x: boolean) {
        this.onlyUsedInSafetyScore = x;
        if (this.onlyUsedInSafetyScore) {

            this.inspFirstFilter = null;
            this.inspSecondFilter = null;
            this.incFirstFilter = null;
            this.incSecondFilter = null;
            this.detFirstFilter = null;
            this.detSecondFilter = null;
            this.defsFirstFilter = null;
            this.defsSecondFilter = null;
            this.selectedDateRanges = [];

            this.closeAllFilters();
        }
        this.reloadFilters();
    }

    public closeAllFilters() {
        this.showInspFilter = false;
        this.showIncFilter = false;
        this.showDetFilter = false;
        this.showDefsFilter = false;
        this.showDateRangeFilter = false;
    }

    public clearAllFilters() {
        this.inspFirstFilter = null;
        this.incFirstFilter = null;
        this.incSecondFilter = null;
        this.detFirstFilter = null;
        this.detSecondFilter = null;
        this.defsFirstFilter = null;
        this.defsSecondFilter = null;
        this.mobileDateRanges = [];
        this.selectedDateRanges = [];
        this.defsIsApplied = false;
        this.detIsApplied = false;
        this.inspIsApplied = false;
        this.incIsApplied = false;

        this.reloadFilters();
    }

    @computedFrom(Reflection.getPropertyPath<CompanyVesselPerformance>(z => z.showInspFilter),
    Reflection.getPropertyPath<CompanyVesselPerformance>(z => z.showIncFilter),
    Reflection.getPropertyPath<CompanyVesselPerformance>(z => z.showDetFilter),
    Reflection.getPropertyPath<CompanyVesselPerformance>(z => z.showDefsFilter),
    Reflection.getPropertyPath<CompanyVesselPerformance>(z => z.showDateRangeFilter))
    public get mobileFilterSelected() {
        return this.showInspFilter || this.showIncFilter || this.showDetFilter || this.showDefsFilter || this.showDateRangeFilter;
    }

    //clear: clear filter value, reset: used for mobile to remove unapplied values, apply: apply the the filter values
    public selectedFilter: { title: string, clear: () => void, reset: () => void, apply: () => void };

    public goBackToFilters() {
        if (this.selectedFilter)
            this.selectedFilter.reset();
        this.closeAllFilters();
        this.selectedFilter = null;
    }

    onMobileApply() {
        if (this.selectedFilter) {
            this.selectedFilter.apply();
            this.selectedFilter = null;
            this.closeAllFilters();
        } else {
            this.closeAllFilters();
            this.toggleMobileFilterDialog();
        }
    }

    //#region insp filter
    public inspFirstFilter: number;
    public inspSecondFilter: number;
    public mobileInspFilter: { low: number, high: number };
    public inspDefaults: { low: number, high: number } = { low: 0, high: 500 };
    public showInspFilter: boolean = false;
    public inspIsApplied: boolean = false;

    public toggleInspFilters() {
        if (!this.showInspFilter) {
            this.closeAllFilters();
        }
        this.showInspFilter = !this.showInspFilter;
        this.selectedFilter = {
            title: "Insp",
            clear: () => {
                this.clearInspFilter();
            },
            reset: () => {
                this.mobileInspFilter = RangeSelectorHelper.resetRangeFilter(this.inspFirstFilter, this.inspSecondFilter, this.inspDefaults);
            },
            apply: () => {
                this.applyInspFilter(this.mobileInspFilter);
            }
        }
    }

    public applyInspFilter(filterValue: { low: number, high: number } = null) {
        if (filterValue) {
            this.inspFirstFilter = filterValue.low;
            this.inspSecondFilter = filterValue.high;
            this.inspIsApplied = true;
        }

        this.reloadFilters();
        this.toggleInspFilters();
    }

    public clearInspFilter() {
        this.inspFirstFilter = null;
        this.inspSecondFilter = null;
        this.mobileInspFilter = { low: null, high: null };
        this.inspIsApplied = false;
        this.reloadFilters();
    }

    @computedFrom("inspIsApplied", "inspFirstFilter", "inspSecondFilter")
    public get inspFilterActive() {
        return this.inspIsApplied && ((this.inspFirstFilter && this.inspFirstFilter >= this.inspDefaults.low) || (this.inspSecondFilter && this.inspSecondFilter <= this.inspDefaults.high));
    }

    //#endregion

    //#region insp filter
    public incFirstFilter: number;
    public incSecondFilter: number;
    public mobileIncFilter: { low: number, high: number };
    public incDefaults: { low: number, high: number } = { low: 0, high: 500 };
    public showIncFilter: boolean = false;
    public incIsApplied: boolean = false;

    public toggleIncFilters() {
        if (!this.showIncFilter) {
            this.closeAllFilters();
        }
        this.showIncFilter = !this.showIncFilter;
        this.selectedFilter = {
            title: "Inc",
            clear: () => {
                this.clearIncFilter();
            },
            reset: () => {
                this.mobileIncFilter = RangeSelectorHelper.resetRangeFilter(this.incFirstFilter, this.incSecondFilter, this.incDefaults);
            },
            apply: () => {
                this.applyIncFilter(this.mobileIncFilter);
            }
        }
    }

    public applyIncFilter(filterValue: { low: number, high: number } = null) {
        if (filterValue) {
            this.incFirstFilter = filterValue.low;
            this.incSecondFilter = filterValue.high;
            this.incIsApplied = true;
        }

        this.reloadFilters();
        this.toggleIncFilters();
    }

    public clearIncFilter() {
        this.incFirstFilter = null;
        this.incSecondFilter = null;
        this.mobileIncFilter = { low: null, high: null };
        this.incIsApplied = false;
        this.reloadFilters();
    }

    @computedFrom("incIsApplied", "incFirstFilter", "incSecondFilter")
    public get incFilterActive() {
        return this.incIsApplied && ((this.incFirstFilter && this.incFirstFilter >= this.incDefaults.low) || (this.incSecondFilter && this.incSecondFilter <= this.incDefaults.high));
    }

    //#endregion

    //#region det filter
    public detFirstFilter: number;
    public detSecondFilter: number;
    public mobileDetFilter: { low: number, high: number };
    public detDefaults: { low: number, high: number } = { low: 0, high: 500 };
    public showDetFilter: boolean = false;
    public detIsApplied: boolean = false;

    public toggleDetFilters() {
        if (!this.showDetFilter) {
            this.closeAllFilters();
        }
        this.showDetFilter = !this.showDetFilter;
        this.selectedFilter = {
            title: "Det",
            clear: () => {
                this.clearDetFilter();
            },
            reset: () => {
                this.mobileDetFilter = RangeSelectorHelper.resetRangeFilter(this.detFirstFilter, this.detSecondFilter, this.detDefaults);
            },
            apply: () => {
                this.applyDetFilter(this.mobileDetFilter);
            }
        }
    }

    public applyDetFilter(filterValue: { low: number, high: number } = null) {
        if (filterValue) {
            this.detFirstFilter = filterValue.low;
            this.detSecondFilter = filterValue.high;
            this.detIsApplied = true;
        }
        this.reloadFilters();
        this.toggleDetFilters();
    }

    public clearDetFilter() {
        this.detFirstFilter = null;
        this.detSecondFilter = null;
        this.mobileDetFilter = { low: null, high: null };
        this.detIsApplied = false;
        this.reloadFilters();
    }

    @computedFrom("detIsApplied", "detFirstFilter", "detSecondFilter")
    public get detFilterActive() {
        return this.detIsApplied && ((this.detFirstFilter && this.detFirstFilter >= 0) || (this.detSecondFilter && this.detSecondFilter <= 550000));
    }

    //#endregion

    //#region defs filter
    public defsFirstFilter: number;
    public defsSecondFilter: number;
    public mobileDefsFilter: { low: number, high: number };
    public defsDefaults: { low: number, high: number } = { low: 0, high: 500 };
    public showDefsFilter: boolean = false;
    public defsIsApplied: boolean = false;

    public toggleDefsFilters() {
        if (!this.showDefsFilter) {
            this.closeAllFilters();
        }
        this.showDefsFilter = !this.showDefsFilter;
        this.selectedFilter = {
            title: "Defs",
            clear: () => {
                this.clearDefsFilter();
            },
            reset: () => {
                this.mobileDefsFilter = RangeSelectorHelper.resetRangeFilter(this.defsFirstFilter, this.defsSecondFilter, this.defsDefaults);
            },
            apply: () => {
                this.applyDefsFilter(this.mobileDefsFilter);
            }
        }
    }

    public applyDefsFilter(filterValue: { low: number, high: number } = null) {
        if (filterValue) {
            this.defsFirstFilter = filterValue.low;
            this.defsSecondFilter = filterValue.high;
            this.defsIsApplied = true;
        }
        this.reloadFilters();
        this.toggleDefsFilters();
    }

    public clearDefsFilter() {
        this.defsFirstFilter = null;
        this.defsSecondFilter = null;
        this.mobileDefsFilter = { low: null, high: null };
        this.defsIsApplied = false;
        this.reloadFilters();
    }

    @computedFrom("defsIsApplied", "defsFirstFilter", "defsSecondFilter")
    public get defsFilterActive() {
        return this.defsIsApplied && ((this.defsFirstFilter && this.defsFirstFilter >= 0) || (this.defsSecondFilter && this.defsSecondFilter <= 550000));
    }

    //#endregion

    //#region date range filter
    public selectedDateRanges: number[] = [];
    public mobileDateRanges: number[] = [];
    public showDateRangeFilter: boolean = false;

    public dateRanges =
        [
            { id: 1, name: '0 - 6 months', dateFrom: moment().add(-6, 'M').startOf('month'), dateTo: moment() },
            { id: 2, name: '6 - 12 months', dateFrom: moment().add(-12, 'M').startOf('month'), dateTo: moment().add(-6, 'M').endOf('month') },
            { id: 3, name: '12 - 24 months', dateFrom: moment().add(-24, 'M').startOf('month'), dateTo: moment().add(-12, 'M').endOf('month') },
            { id: 4, name: '24 - 36 months', dateFrom: moment().add(-36, 'M').startOf('month'), dateTo: moment().add(-24, 'M').endOf('month') },
            { id: 5, name: '36 - 48 months', dateFrom: moment().add(-48, 'M').startOf('month'), dateTo: moment().add(-36, 'M').endOf('month') },
            { id: 6, name: '48 - 60 months', dateFrom: moment().add(-60, 'M').startOf('month'), dateTo: moment().add(-48, 'M').endOf('month') }
        ];

    public toggleDateRangeFilter() {
        if (!this.showDateRangeFilter) {
            this.closeAllFilters();
        }
        this.showDateRangeFilter = !this.showDateRangeFilter;
        this.selectedFilter = {
            title: "Date Range",
            clear: () => {
                this.clearDateRangeFilter();
            },
            reset: () => {
                this.mobileDateRanges = CloneHelper.cloneObject(this.selectedDateRanges);
            },
            apply: () => {
                this.applyDateRangeFilter(this.mobileDateRanges.map(d => { return { id: d }; }));
            }
        }
    }

    public applyDateRangeFilter(items: any[] = null) {
        if (JSON.stringify(this.selectedDateRanges) == JSON.stringify(items.map(x => x.id)))
            return;

        if (items) {
            this.selectedDateRanges = items.map(x => x.id);
        }
        this.reloadFilters();
        this.showDateRangeFilter = false;
    }

    public clearDateRangeFilter() {
        this.selectedDateRanges = [];
        this.mobileDateRanges = [];
        this.reloadFilters();
    }

    @computedFrom("selectedDateRanges", "selectedDateRanges.length")
    public get dateRangeFilterActive() {
        return this.selectedDateRanges && (this.selectedDateRanges.length > 0);
    }
    //#endregion

    //#region mobile filters
    isMobileFilterOpen: boolean = false;

    toggleMobileFilterDialog() {
        this.isMobileFilterOpen = !this.isMobileFilterOpen;
    }

    //#endregion

    public age(item: any) {
        if (item) {
            if (item.dateOfBuild) {
                return VesselHelper.getAgeFromDate(item.dateOfBuild);
            } else {
                return ""
            }
        } else {
            return null;
        }
    }

    isDownloadingExcel: boolean = false;
    async downloadExcel() {
        if (this.isDownloadingExcel) return;

        this.isDownloadingExcel = true;
        try {
            const request = this.getFilters();
            await this.companyClientService.downloadDocFleet(request);
        } catch (e) {
            console.log("Failed to download doc fleet", e);
        }
        this.isDownloadingExcel = false;
    }
}
